/**
 * Admin waitlist queue. Plan §Capacity safety valve.
 *
 * Two lists side-by-side: pending entries the admin should invite next
 * (sorted by arrival, FIFO), and the most-recent 50 already-invited
 * entries for operational visibility ("did yesterday's batch go out?").
 *
 * The "Mark invited" button is the entire workflow at MVP — the admin
 * sends the invitation email by hand (Gmail, SES console, whatever)
 * and then flips the row's `invited_at` so they don't re-send. Phase 11
 * wires SES + a template + an auto-send button. Until then the manual
 * two-step is honest: we want a human to look at each entry before
 * inviting anyway.
 */

import { useMarkInvitedMutation, useWaitlistQuery } from '../api/waitlist.js';

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function WaitlistRoute() {
  const list = useWaitlistQuery();

  return (
    <div>
      <header className="mb-6">
        <h2 className="font-display font-semibold text-2xl text-neutral-900">Waitlist</h2>
        <p className="mt-1 text-neutral-600 text-sm">
          People waiting for capacity. Send the invitation email by hand (Gmail / SES console), then
          click <span className="font-medium">Mark invited</span> here so we don't re-send. Invites
          should go in arrival order — top of the list first.
        </p>
      </header>

      {list.isLoading ? (
        <p className="text-neutral-500 text-sm">Loading…</p>
      ) : list.isError ? (
        <p className="text-danger-700 text-sm">Couldn't load the waitlist.</p>
      ) : list.data ? (
        <>
          <Counters counts={list.data.counts} />
          <PendingTable rows={list.data.pending} />
          <InvitedTable rows={list.data.recentlyInvited} />
        </>
      ) : null}
    </div>
  );
}

function Counters({ counts }: { counts: { pending: number; invited: number; joined: number } }) {
  return (
    <section className="mb-6 grid grid-cols-3 gap-3">
      <Counter label="Pending" value={counts.pending} tone="primary" />
      <Counter label="Invited" value={counts.invited} tone="neutral" />
      <Counter label="Joined" value={counts.joined} tone="emerald" />
    </section>
  );
}

function Counter({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'primary' | 'neutral' | 'emerald';
}) {
  const toneClass =
    tone === 'primary'
      ? 'border-primary-200 bg-primary-50 text-primary-900'
      : tone === 'emerald'
        ? 'border-success-200 bg-success-50 text-success-900'
        : 'border-neutral-200 bg-white text-neutral-900';
  return (
    <div className={`rounded-md border p-4 ${toneClass}`}>
      <p className="text-xs uppercase tracking-wide opacity-70">{label}</p>
      <p className="mt-1 font-display font-semibold text-3xl">{value}</p>
    </div>
  );
}

function PendingTable({
  rows,
}: {
  rows: Array<{ id: string; email: string; ipCountry: string | null; createdAt: string }>;
}) {
  const mark = useMarkInvitedMutation();
  return (
    <section className="mt-2">
      <h3 className="mb-2 font-medium text-neutral-900">Pending ({rows.length})</h3>
      {rows.length === 0 ? (
        <p className="rounded-md border border-neutral-200 bg-neutral-50 p-4 text-neutral-500 text-sm">
          Nobody's waiting right now.
        </p>
      ) : (
        <div className="overflow-hidden rounded-md border border-neutral-200">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left text-neutral-500 text-xs uppercase">
              <tr>
                <th className="px-4 py-2 font-medium">#</th>
                <th className="px-4 py-2 font-medium">Email</th>
                <th className="px-4 py-2 font-medium">Country</th>
                <th className="px-4 py-2 font-medium">Joined waitlist</th>
                <th className="px-4 py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} className="border-neutral-200 border-t">
                  <td className="px-4 py-2 text-neutral-500">{i + 1}</td>
                  <td className="px-4 py-2 font-mono text-neutral-900 text-xs">{r.email}</td>
                  <td className="px-4 py-2 text-neutral-600">{r.ipCountry ?? '—'}</td>
                  <td className="px-4 py-2 text-neutral-600">{formatTimestamp(r.createdAt)}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => mark.mutate(r.id)}
                      disabled={mark.isPending && mark.variables === r.id}
                      className="rounded-md border border-primary-300 bg-white px-3 py-1.5 font-medium text-primary-700 text-xs hover:bg-primary-50 disabled:opacity-60"
                    >
                      {mark.isPending && mark.variables === r.id ? 'Marking…' : 'Mark invited'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function InvitedTable({
  rows,
}: {
  rows: Array<{
    id: string;
    email: string;
    ipCountry: string | null;
    createdAt: string;
    invitedAt: string;
    joinedUserId: string | null;
  }>;
}) {
  return (
    <section className="mt-8">
      <h3 className="mb-2 font-medium text-neutral-900">
        Recently invited <span className="text-neutral-500 text-sm">(last 50)</span>
      </h3>
      {rows.length === 0 ? (
        <p className="rounded-md border border-neutral-200 bg-neutral-50 p-4 text-neutral-500 text-sm">
          No invites sent yet.
        </p>
      ) : (
        <div className="overflow-hidden rounded-md border border-neutral-200">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left text-neutral-500 text-xs uppercase">
              <tr>
                <th className="px-4 py-2 font-medium">Email</th>
                <th className="px-4 py-2 font-medium">Country</th>
                <th className="px-4 py-2 font-medium">Invited</th>
                <th className="px-4 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-neutral-200 border-t">
                  <td className="px-4 py-2 font-mono text-neutral-900 text-xs">{r.email}</td>
                  <td className="px-4 py-2 text-neutral-600">{r.ipCountry ?? '—'}</td>
                  <td className="px-4 py-2 text-neutral-600">{formatTimestamp(r.invitedAt)}</td>
                  <td className="px-4 py-2">
                    {r.joinedUserId ? (
                      <span className="rounded bg-success-50 px-2 py-0.5 font-medium text-success-800 text-xs">
                        Joined
                      </span>
                    ) : (
                      <span className="rounded bg-neutral-100 px-2 py-0.5 font-medium text-neutral-700 text-xs">
                        Invite sent
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
